import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LanguageInterceptor } from './core/interceptors/language.interceptor';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { provideFirebaseApp } from '@angular/fire/app';
import { provideMessaging } from '@angular/fire/messaging';
import { getMessaging } from '@firebase/messaging';
import { initializeApp } from 'firebase/app';
import { environment } from './environment/environment';
import { lastValueFrom, of, timeout } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PlatformThemeService } from './core/services/platform-theme.service';
import { SubdomainService } from './core/services/subdomain.service';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './i18n/', '.json');
}

export function initTranslations(translate: TranslateService) {
  return () => {
    const lang = localStorage.getItem('lang') || 'en-US';
    translate.setDefaultLang(lang);
    return lastValueFrom(translate.use(lang));
  };
}

export function initPlatformTheme(
  platformThemeService: PlatformThemeService,
  subdomainService: SubdomainService
) {
  return () => {
    const sub = subdomainService.extractSubdomain();
    if (sub) {
      return lastValueFrom(
        platformThemeService.loadPlatformBySubdomain(sub).pipe(
          timeout({ first: 3500 }),
          catchError(() => of(null))
        )
      );
    }
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    MessageService,
    {
      provide: APP_INITIALIZER,
      useFactory: initTranslations,
      deps: [TranslateService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initPlatformTheme,
      deps: [PlatformThemeService, SubdomainService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideNativeDateAdapter(),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideMessaging(() => getMessaging()),
    importProvidersFrom([
      ButtonModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ]),
    provideRouter(routes),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([loadingInterceptor, authInterceptor])
    ),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          cssLayer: {
            darkModeSelector: '.fake-dark-selector',
          },
        },
      },
    }),
    provideAnimationsAsync(),
  ],
};
